/**
 * Checkbox — three-state (on / off / indeterminate), used everywhere from forms
 * to the table's select-all header cell.
 */

import { forwardRef, useId } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import * as RCheckbox from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/cn';

export type CheckedState = boolean | 'indeterminate';

/**
 * The mark is sized for the row it sits in; the *target* is sized for a finger.
 * A 16px box is right on a 40px grid row and wrong under WCAG 2.2 SC 2.5.8,
 * which wants 24×24 — and the spacing exception does not apply here, because the
 * neighbouring target is the row itself, activated by the same click region. The
 * pseudo-element extends the hit area without moving a pixel on screen.
 */
const BOX: Record<'sm' | 'md', string> = {
  // 6px, not 4: `inset` resolves against the *padding* box, and the 1px border
  // is outside it, so -4px on the 16px control measured 22×22 — still short.
  sm: "relative size-3.5 before:absolute before:-inset-1.5 before:content-['']",
  md: "relative size-4 before:absolute before:-inset-1.5 before:content-['']",
};

const MARK: Record<'sm' | 'md', string> = {
  sm: 'size-2.5',
  md: 'size-3',
};

export interface CheckboxProps
  extends Omit<
    ComponentPropsWithoutRef<typeof RCheckbox.Root>,
    'checked' | 'onCheckedChange' | 'children'
  > {
  checked?: CheckedState;
  onCheckedChange?: (checked: CheckedState) => void;
  /** Renders the box inside a `<label>` with this copy beside it. */
  label?: ReactNode;
  description?: ReactNode;
  size?: 'sm' | 'md';
  /** Required when there is no `label` — an unnamed checkbox is a reject. */
  'aria-label'?: string;
  className?: string;
  containerClassName?: string;
  /**
   * Merged onto the `<label>`, last. The default ink is `text-primary`, which is
   * right for a checkbox that carries the row's meaning (a table's select-all, a
   * settings toggle) and wrong for an optional preference sitting beside the
   * fields it qualifies — there it has to be a step *under* the field labels,
   * not a step over them. Pass `text-secondary` for that case.
   */
  labelClassName?: string;
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  {
    checked,
    onCheckedChange,
    label,
    description,
    size = 'md',
    disabled = false,
    className,
    containerClassName,
    labelClassName,
    id,
    ...rest
  },
  ref,
) {
  const generated = useId();
  const checkboxId = id ?? generated;

  const box = (
    <RCheckbox.Root
      ref={ref}
      id={checkboxId}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(
        // `--line-object`, not `--line-strong`. An UNCHECKED box has no fill and
        // no mark: the border is its entire rendering, so it is the boundary
        // WCAG 1.4.11 owes 3:1 — and `--line-strong` (0.24) measured 1.61:1 in
        // light and 1.66:1 in dark, i.e. the control was drawn at half the floor
        // the same screen's text inputs were already raised to. `--line-object`
        // (0.50) is the step that clears 3:1 on `--surface-card`, `--surface-2`
        // and `--surface-1` at once; `scripts/contrast-check.mjs` gates the
        // unchecked box against all three grounds and against its own fill.
        'grid shrink-0 place-items-center rounded-xs! border border-line-object bg-surface-1',
        'transition-[background-color,border-color,color] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        BOX[size],
        !disabled && 'hover:border-focus',
        /*
          `--focus`, not `--accent`.
          The filled box used to be gold, on the reading that a control's "on"
          state is one of gold's jobs. Measured on a list, that reading does not
          survive contact: /companies carried the create button, the current-page
          rail, the sort arrow, the menu ticks, the row checkboxes and the paging
          bar in one hue at once, and a mark with six jobs signals none of them.
          Gold is spent on create and nothing else now. Selection belongs to
          `--focus`, which is already the token for "the thing the reader is on",
          and it pays its own boundary: #5b9cec measures 6.4:1 on `--surface-1`
          in dark and #164aae 8.6:1 in light, against the 3:1 WCAG 1.4.11 owes a
          filled control — so no separate `--accent-edge` step is needed. The
          mark inside takes `--text-inverse`, which is the ink of every inverted
          ground in the product (#081527 on the light-blue dark fill, #ffffff on
          the deep-blue light one): 6.46:1 and 8.00:1.
        */
        'data-[state=checked]:border-focus data-[state=checked]:bg-focus data-[state=checked]:text-inverse',
        'data-[state=indeterminate]:border-focus data-[state=indeterminate]:bg-focus data-[state=indeterminate]:text-inverse',
        // Disabled must beat every state variant, so it is stacked on each.
        'disabled:cursor-not-allowed disabled:border-line-subtle disabled:bg-surface-3 disabled:text-muted',
        'disabled:data-[state=checked]:border-line-subtle disabled:data-[state=checked]:bg-surface-3 disabled:data-[state=checked]:text-muted',
        'disabled:data-[state=indeterminate]:border-line-subtle disabled:data-[state=indeterminate]:bg-surface-3 disabled:data-[state=indeterminate]:text-muted',
        className,
      )}
      {...rest}
    >
      <RCheckbox.Indicator className="grid place-items-center">
        {checked === 'indeterminate' ? (
          <Minus aria-hidden="true" className={MARK[size]} strokeWidth={3.25} />
        ) : (
          <Check aria-hidden="true" className={MARK[size]} strokeWidth={3.25} />
        )}
      </RCheckbox.Indicator>
    </RCheckbox.Root>
  );

  if (!label && !description) return box;

  return (
    <div className={cn('flex items-start gap-2', containerClassName)}>
      {box}
      <div className="min-w-0 select-none">
        {label ? (
          // `htmlFor` on a button is valid: buttons are labelable elements, so
          // clicking the copy toggles the box.
          <label
            htmlFor={checkboxId}
            className={cn(
              'block text-base leading-4',
              disabled ? 'cursor-not-allowed text-muted' : 'cursor-pointer text-primary',
              labelClassName,
            )}
          >
            {label}
          </label>
        ) : null}
        {description ? (
          <p className="mt-1 text-xs text-muted leading-snug">{description}</p>
        ) : null}
      </div>
    </div>
  );
});
